import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/bporh_admissoes.css';
import '../styles/bporh_conversas.css';

// Conversas do BPO RH — arquivo do atendimento (Slack/Discord), lendo o
// espelho local (tabelas conversa_*). A página NUNCA fala com o Slack:
// quem fala é o sync_slack.py (agendado ou pelo botão Atualizar).
// Isolamento por cliente: toda chamada leva cliente_id — cada cliente é um
// workspace separado e as conversas jamais se misturam.

// ─── badge vermelho no favicon (igual ao Slack) ───
// Desenha uma bolinha vermelha com o nº de não lidas em cima do ícone da aba.
// Fora do componente: o estado do favicon é da PÁGINA (document), não do React.
let _favLink = null;          // <link rel="icon"> em uso
let _favOriginal = null;      // href original, para restaurar
let _favImg = null;           // ícone base carregado (cacheado)

const _linkFavicon = () => {
  if (_favLink) return _favLink;
  _favLink = document.querySelector("link[rel~='icon']");
  if (!_favLink) {
    _favLink = document.createElement('link');
    _favLink.rel = 'icon';
    document.head.appendChild(_favLink);
  }
  // o app não declara <link rel="icon">: o ícone real da aba (o foguetinho) é
  // o /favicon.ico servido por convenção — é ele o original e a base do badge
  if (_favOriginal === null) _favOriginal = _favLink.href || '/favicon.ico';
  return _favLink;
};

const desenharFaviconBadge = (n) => {
  const link = _linkFavicon();
  if (!n) { link.href = _favOriginal; return; }
  const pintar = (img) => {
    try {
      const cv = document.createElement('canvas');
      cv.width = 64; cv.height = 64;
      const cx = cv.getContext('2d');
      if (img) cx.drawImage(img, 0, 0, 64, 64);
      // bolinha no canto inferior direito — vermelho do Slack (#e01e5a)
      cx.beginPath();
      cx.arc(44, 44, 20, 0, Math.PI * 2);
      cx.fillStyle = '#e01e5a';
      cx.fill();
      cx.lineWidth = 4;
      cx.strokeStyle = '#1a1d21';
      cx.stroke();
      cx.fillStyle = '#fff';
      cx.font = '900 24px Lato, Arial, sans-serif';
      cx.textAlign = 'center';
      cx.textBaseline = 'middle';
      cx.fillText(n > 9 ? '9+' : String(n), 44, 46);
      link.href = cv.toDataURL('image/png');
    } catch { /* canvas bloqueado: fica o ícone normal */ }
  };
  if (_favImg) { pintar(_favImg); return; }
  // base do badge = o ícone REAL da aba (o foguetinho do favicon.ico);
  // se o .ico não decodificar no canvas, cai no PNG e, por fim, só a bolinha
  const img = new Image();
  img.src = '/favicon.ico';
  img.onload = () => { _favImg = img; pintar(img); };
  img.onerror = () => {
    const png = new Image();
    png.src = '/icon-192.png';
    png.onload = () => { _favImg = png; pintar(png); };
    png.onerror = () => pintar(null);   // sem ícone base: só a bolinha
  };
};

// Paleta do tema escuro (padrão Slack aubergine — ver bporh_conversas.css):
// equipe no roxo Slack, cliente no grafite, bot num roxo apagado.
const CORES_AUTOR = {
  equipe: { bg: '#611f69', cor: '#fff', rotulo: 'Equipe Comece' },
  cliente: { bg: '#222529', cor: '#d1d2d3', rotulo: 'Cliente' },
  bot: { bg: '#3a2d42', cor: '#d9b8ff', rotulo: 'Bot / integração' },
};

// ─── Emojis do Slack ───
// O Slack guarda emoji como :shortcode: (":grin:", ":+1::skin-tone-2:"). A tabela
// cobre os mais usados no atendimento; shortcode desconhecido (emoji custom da
// empresa) continua aparecendo como :nome:, para não sumir informação.
const EMOJI_SLACK = {
  grin: '😁', smile: '😄', smiley: '😃', blush: '😊', joy: '😂', rofl: '🤣', laughing: '😆',
  wink: '😉', heart_eyes: '😍', kissing_heart: '😘', slightly_smiling_face: '🙂', relaxed: '☺️',
  sweat_smile: '😅', grinning: '😀', upside_down_face: '🙃', thinking_face: '🤔', neutral_face: '😐',
  expressionless: '😑', disappointed: '😞', cry: '😢', sob: '😭', worried: '😟', confused: '😕',
  sweat: '😓', tired_face: '😫', weary: '😩', scream: '😱', flushed: '😳', sunglasses: '😎',
  nerd_face: '🤓', innocent: '😇', star_struck: '🤩', partying_face: '🥳', hugging_face: '🤗',
  face_with_hand_over_mouth: '🤭', shushing_face: '🤫', zipper_mouth_face: '🤐', yum: '😋',
  stuck_out_tongue: '😛', stuck_out_tongue_winking_eye: '😜', roll_eyes: '🙄', grimacing: '😬',
  face_with_rolling_eyes: '🙄', pleading_face: '🥺', melting_face: '🫠', saluting_face: '🫡',
  '+1': '👍', thumbsup: '👍', '-1': '👎', thumbsdown: '👎', ok_hand: '👌', clap: '👏', pray: '🙏',
  raised_hands: '🙌', wave: '👋', muscle: '💪', point_up: '☝️', point_right: '👉', point_left: '👈',
  point_down: '👇', v: '✌️', crossed_fingers: '🤞', handshake: '🤝', eyes: '👀', see_no_evil: '🙈',
  hear_no_evil: '🙉', speak_no_evil: '🙊', facepalm: '🤦', man_facepalming: '🤦‍♂️',
  woman_facepalming: '🤦‍♀️', shrug: '🤷', man_shrugging: '🤷‍♂️', woman_shrugging: '🤷‍♀️',
  heart: '❤️', blue_heart: '💙', green_heart: '💚', yellow_heart: '💛', purple_heart: '💜',
  orange_heart: '🧡', black_heart: '🖤', white_heart: '🤍', broken_heart: '💔', sparkling_heart: '💖',
  heartpulse: '💗', two_hearts: '💕', fire: '🔥', star: '⭐', star2: '🌟', sparkles: '✨', zap: '⚡',
  boom: '💥', tada: '🎉', confetti_ball: '🎊', balloon: '🎈', gift: '🎁', trophy: '🏆', medal: '🏅',
  first_place_medal: '🥇', '100': '💯', rocket: '🚀', airplane: '✈️', car: '🚗', red_car: '🚗',
  bus: '🚌', house: '🏠', office: '🏢', flag_br: '🇧🇷', 'flag-br': '🇧🇷', brazil: '🇧🇷',
  white_check_mark: '✅', heavy_check_mark: '✔️', ballot_box_with_check: '☑️', x: '❌',
  negative_squared_cross_mark: '❎', warning: '⚠️', exclamation: '❗', question: '❓', grey_question: '❔',
  heavy_exclamation_mark: '❗', bangbang: '‼️', interrobang: '⁉️', no_entry: '⛔', no_entry_sign: '🚫',
  hourglass: '⌛', hourglass_flowing_sand: '⏳', alarm_clock: '⏰', clock1: '🕐', calendar: '📅',
  date: '📅', spiral_calendar_pad: '🗓️', pushpin: '📌', paperclip: '📎', memo: '📝', pencil: '📝',
  page_facing_up: '📄', clipboard: '📋', bookmark_tabs: '📑', file_folder: '📁', open_file_folder: '📂',
  email: '📧', 'e-mail': '📧', envelope: '✉️', inbox_tray: '📥', outbox_tray: '📤', mailbox: '📫',
  phone: '📞', telephone_receiver: '📞', iphone: '📱', computer: '💻', desktop_computer: '🖥️',
  moneybag: '💰', dollar: '💵', money_with_wings: '💸', credit_card: '💳', chart_with_upwards_trend: '📈',
  chart_with_downwards_trend: '📉', bar_chart: '📊', bulb: '💡', mag: '🔍', wrench: '🔧', hammer: '🔨',
  gear: '⚙️', lock: '🔒', unlock: '🔓', key: '🔑', bell: '🔔', loudspeaker: '📢', mega: '📣',
  speech_balloon: '💬', thought_balloon: '💭', coffee: '☕', pizza: '🍕', beer: '🍺', beers: '🍻',
  cake: '🎂', birthday: '🎂', sun_with_face: '🌞', sunny: '☀️', cloud: '☁️', rain_cloud: '🌧️',
  umbrella: '☔', palm_tree: '🌴', beach_with_umbrella: '🏖️', desert_island: '🏝️', earth_americas: '🌎',
  skull: '💀', ghost: '👻', robot_face: '🤖', alien: '👽', dog: '🐶', cat: '🐱', eyes_left: '👀',
  rotating_light: '🚨', ambulance: '🚑', hospital: '🏥', pill: '💊', syringe: '💉', mask: '😷',
  face_with_thermometer: '🤒', sleeping: '😴', zzz: '💤', running: '🏃', walking: '🚶',
  man_running: '🏃‍♂️', woman_running: '🏃‍♀️', dancer: '💃', man_dancing: '🕺', tophat: '🎩',
  crown: '👑', gem: '💎', arrow_right: '➡️', arrow_left: '⬅️', arrow_up: '⬆️', arrow_down: '⬇️',
  arrows_counterclockwise: '🔄', repeat: '🔁', fast_forward: '⏩', rewind: '⏪', heavy_plus_sign: '➕',
  heavy_minus_sign: '➖', heavy_dollar_sign: '💲', copyright: '©️', tm: '™️', pencil2: '✏️',
  black_square_button: '🔲', white_square_button: '🔳', red_circle: '🔴', large_blue_circle: '🔵',
  large_green_circle: '🟢', large_yellow_circle: '🟡', large_orange_circle: '🟠', white_circle: '⚪',
  black_circle: '⚫', hand: '✋', raised_hand: '✋', open_hands: '👐', fist: '✊', punch: '👊',
  metal: '🤘', call_me_hand: '🤙', ok: '🆗', new: '🆕', free: '🆓', cool: '🆒', sos: '🆘',
  santa: '🎅', christmas_tree: '🎄', jack_o_lantern: '🎃', soccer: '⚽', basketball: '🏀',
  tennis: '🎾', video_game: '🎮', dart: '🎯', musical_note: '🎵', notes: '🎶', microphone: '🎤',
  headphones: '🎧', tv: '📺', camera: '📷', movie_camera: '🎥', clapper: '🎬', bookmark: '🔖',
  link: '🔗', wastebasket: '🗑️', hankey: '💩', poop: '💩', smiling_imp: '😈', rage: '😡', angry: '😠',
  triumph: '😤', unamused: '😒', hushed: '😯', open_mouth: '😮', astonished: '😲', dizzy_face: '😵',
  exploding_head: '🤯', cowboy_hat_face: '🤠', clown_face: '🤡', money_mouth_face: '🤑',
  handshake_tone2: '🤝', ok_woman: '🙆‍♀️', ok_man: '🙆‍♂️', no_good: '🙅', raising_hand: '🙋',
  bow: '🙇', person_with_folded_hands: '🙏', thumbsup_all: '👍', check: '✅', heavy_multiplication_x: '✖️',
};
const SKIN_TONE = /::skin-tone-[2-6]:/g;
// Troca :shortcode: por emoji; o texto continua sendo texto (sem HTML).
const emojiSlack = (t) => {
  if (!t) return t;
  return String(t).replace(SKIN_TONE, ':').replace(/:([a-z0-9_+-]+):/gi, (m, nome) => EMOJI_SLACK[nome.toLowerCase()] || m);
};
// Emojis mais usados no atendimento, para o seletor da caixa de resposta.
const EMOJIS_RAPIDOS = ['😊', '😄', '😉', '🙂', '😅', '😂', '🤔', '😢', '🙏', '👍', '👏', '🙌', '👋', '💪', '🤝',
  '❤️', '💙', '✅', '❌', '⚠️', '❗', '❓', '🔥', '🎉', '⭐', '✨', '💡', '📌', '📎', '📝',
  '📅', '⏰', '📧', '📞', '💰', '📊', '🚀', '☕', '🎂', '🏖️'];

// ─── Áudio recebido pelo Slack ───
// O arquivo mora só no Slack: ao clicar, busca pelo proxy do backend (com o
// JWT) e toca no navegador; nada fica salvo no servidor.
const AudioAnexo = ({ api, headers, clienteId, mensagemId, indice, anexo }) => {
  const [src, setSrc] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const carregar = () => {
    setCarregando(true); setErro('');
    axios.get(`${api}/anexo/`, { params: { cliente_id: clienteId, mensagem_id: mensagemId, indice },
      headers, responseType: 'blob' })
      .then((res) => setSrc(URL.createObjectURL(res.data)))
      .catch(() => setErro('Não foi possível buscar o áudio no Slack'))
      .finally(() => setCarregando(false));
  };
  if (src) {
    return <audio controls autoPlay src={src} style={{ height: 36, maxWidth: '100%', marginTop: 6 }} />;
  }
  return (
    <button type="button" className="conv-audio-btn" onClick={carregar} disabled={carregando}
      title={`${anexo.nome || 'áudio'}${anexo.tamanho ? ` · ${Math.round(anexo.tamanho / 1024)} KB` : ''}`}>
      <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-play'} me-2`}></i>
      {carregando ? 'Buscando áudio...' : 'Ouvir áudio'}
      {erro && <span className="ms-2 text-danger">{erro}</span>}
    </button>
  );
};

const BpoRhConversas = () => {
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
  const API = `${process.env.REACT_APP_API_BASE}/api/bporh/conversas`;

  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // O body do app é branco e aparece sempre que a página é mais curta que a
  // janela (ou no overscroll). Enquanto Conversas estiver aberta, o body fica
  // escuro; ao sair da página, volta ao normal.
  useEffect(() => {
    document.body.classList.add('conversas-body');
    return () => document.body.classList.remove('conversas-body');
  }, []);

  // Garante a Lato (fonte do Slack) mesmo sem reiniciar o dev server: o link
  // do index.html só entra em restart; aqui injetamos em tempo de execução.
  useEffect(() => {
    if (document.getElementById('fonte-lato')) return;
    const l = document.createElement('link');
    l.id = 'fonte-lato';
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&display=swap';
    document.head.appendChild(l);
  }, []);

  // ─── clientes com conversas espelhadas ───
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState('');
  // guarda contra corrida: resposta em voo de um cliente antigo não pode
  // pintar a tela do cliente atual (cada cliente é um workspace isolado)
  const clienteIdRef = useRef(clienteId);
  useEffect(() => { clienteIdRef.current = clienteId; }, [clienteId]);
  const carregarClientes = useCallback(() => {
    axios.get(`${API}/clientes/`, { headers: headers() })
      .then((res) => {
        setClientes(res.data.clientes || []);
        // um cliente só: abre direto nele
        if ((res.data.clientes || []).length === 1) {
          setClienteId(String(res.data.clientes[0].cliente_id));
        }
      })
      .catch((e) => setMensagem(`${e.response?.data?.erro || 'Não foi possível carregar os clientes'}`));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregarClientes(); }, [carregarClientes]);

  const clienteAtual = clientes.find(c => String(c.cliente_id) === String(clienteId));

  // ─── conversas do cliente ───
  const [conversas, setConversas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [filtroNome, setFiltroNome] = useState('');
  // Gauss tem uma conta de serviço por OPERADORA (14/10/2026): cada operadora
  // vira uma FICHA própria no topo (GaussFleet · Francielle / · Vitória)
  const [contaSel, setContaSel] = useState('');
  const carregarConversas = useCallback(() => {
    if (!clienteId) { setConversas([]); return; }
    const cid = clienteId;
    setCarregando(true);
    axios.get(`${API}/`, { params: { cliente_id: clienteId, ...(contaSel ? { conta: contaSel } : {}) }, headers: headers() })
      .then((res) => {
        if (clienteIdRef.current !== cid) return;
        setConversas(res.data.conversas || []);
      })
      .catch((e) => setMensagem(`${e.response?.data?.erro || 'Não foi possível carregar as conversas'}`))
      .finally(() => { if (clienteIdRef.current === cid) setCarregando(false); });
  }, [clienteId, contaSel]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { carregarConversas(); }, [carregarConversas]);

  const conversasFiltradas = React.useMemo(() => {
    const b = filtroNome.trim().toLowerCase();
    if (!b) return conversas;
    return conversas.filter(c => (c.nome || '').toLowerCase().includes(b));
  }, [conversas, filtroNome]);

  // ─── não lidas: bolinha verde com o nº de mensagens novas ───
  // 'Visto' = total de mensagens da conversa na última vez que VOCÊ a abriu
  // (guardado no navegador, por cliente). Novas = total de agora − visto.
  // Como o total vem na lista que o polling atualiza a cada 12s, o contador
  // sobe sozinho quando chega mensagem. Marcador por navegador: cada operador
  // tem o seu — não é compartilhado.
  const chaveVistos = `conversas_vistos_${clienteId}`;
  const [vistos, setVistos] = useState({});
  useEffect(() => {
    if (!clienteId) { setVistos({}); return; }
    try { setVistos(JSON.parse(localStorage.getItem(chaveVistos) || '{}')); }
    catch { setVistos({}); }
  }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // conversa nunca vista neste navegador: nasce zerada (sem bolinha) — o
  // contador passa a valer do primeiro acesso em diante
  useEffect(() => {
    if (!clienteId || conversas.length === 0) return;
    setVistos((v) => {
      let mudou = false;
      const novo = { ...v };
      conversas.forEach((c) => {
        if (novo[c.canal_id] === undefined) { novo[c.canal_id] = c.mensagens || 0; mudou = true; }
      });
      if (!mudou) return v;
      try { localStorage.setItem(chaveVistos, JSON.stringify(novo)); } catch { /* ok */ }
      return novo;
    });
  }, [conversas, clienteId, chaveVistos]);

  const novasDe = (c) => Math.max(0, (c.mensagens || 0) - (vistos[c.canal_id] ?? (c.mensagens || 0)));

  // ─── não lidas por EMPRESA (bolinha nas abas do cabeçalho) ───
  // Mesma regra da lista, aplicada a cada cliente: busca a lista de conversas
  // de todos os clientes a cada 30 s e compara com o 'visto' guardado no
  // navegador para aquele cliente. Conversa nunca vista não conta (como na
  // lista). O cliente aberto usa o estado ao vivo, para a bolinha zerar na
  // hora em que a conversa é aberta.
  const [naoLidasPorCliente, setNaoLidasPorCliente] = useState({});
  const contarNaoLidas = useCallback((cid, lista) => {
    let v = {};
    try { v = JSON.parse(localStorage.getItem(`conversas_vistos_${cid}`) || '{}'); } catch { v = {}; }
    return lista.reduce((n, c) => n + Math.max(0, (c.mensagens || 0) - (v[c.canal_id] ?? (c.mensagens || 0))), 0);
  }, []);
  useEffect(() => {
    if (clientes.length === 0) return undefined;
    let vivo = true;
    const buscar = () => {
      clientes.forEach((cl) => {
        const cid = String(cl.cliente_id);
        if (cid === String(clienteIdRef.current)) return;   // o aberto usa o estado ao vivo
        axios.get(`${API}/`, { params: { cliente_id: cid }, headers: headers() })
          .then((res) => {
            if (!vivo) return;
            const n = contarNaoLidas(cid, res.data.conversas || []);
            setNaoLidasPorCliente((prev) => (prev[cid] === n ? prev : { ...prev, [cid]: n }));
          })
          .catch(() => {});
      });
    };
    buscar();
    const t = setInterval(buscar, 30000);
    return () => { vivo = false; clearInterval(t); };
  }, [clientes, contarNaoLidas]); // eslint-disable-line react-hooks/exhaustive-deps
  // cliente aberto: recalcula a partir da lista e dos 'vistos' já em memória
  useEffect(() => {
    if (!clienteId) return;
    const n = conversas.reduce((acc, c) => acc + novasDe(c), 0);
    setNaoLidasPorCliente((prev) => (prev[clienteId] === n ? prev : { ...prev, [clienteId]: n }));
  }, [conversas, vistos, clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── som de mensagem nova ───
  // "Ding" de duas notas gerado no navegador (Web Audio) — sem arquivo de
  // áudio. Toca quando o total de não lidas AUMENTA (mensagem chegou pelo
  // polling); abrir conversa só zera contador, não toca. O navegador exige uma
  // interação antes de tocar som — o primeiro clique na página destrava.
  const [somLigado, setSomLigado] = useState(() => {
    try { return localStorage.getItem('conversas_som') !== '0'; } catch { return true; }
  });
  const audioCtxRef = useRef(null);

  const tocarDing = useCallback(() => {
    try {
      // cria/destrava o contexto NA HORA de tocar — a versão anterior exigia
      // que o destravamento já tivesse acontecido e desistia em silêncio
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      const soar = () => {
        try {
          // duas notas (lá5 -> mi6), envelope suave — knock discreto
          [[880, 0], [1318.5, 0.12]].forEach(([freq, atraso]) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            const t = ctx.currentTime + atraso;
            gain.gain.setValueAtTime(0.0001, t);
            gain.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
            osc.connect(gain).connect(ctx.destination);
            osc.start(t);
            osc.stop(t + 0.35);
          });
        } catch { /* falha de áudio nunca pode quebrar a página */ }
      };
      if (ctx.state === 'suspended') ctx.resume().then(soar).catch(() => {});
      else soar();
    } catch { /* sem áudio no navegador */ }
  }, []);

  // ligar o som pelo sininho TOCA o ding na hora — é o teste do alto-falante
  // (clique é gesto do usuário: o navegador sempre permite) e já destrava o
  // áudio para os toques automáticos seguintes
  const alternarSom = () => {
    setSomLigado((v) => {
      const novo = !v;
      try { localStorage.setItem('conversas_som', novo ? '1' : '0'); } catch { /* ok */ }
      if (novo) setTimeout(tocarDing, 0);
      return novo;
    });
  };

  // qualquer clique na página destrava o áudio (exigência do navegador para
  // os toques automáticos que acontecem sem gesto, vindos do polling)
  useEffect(() => {
    const destravar = () => {
      try {
        if (!audioCtxRef.current) {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (Ctx) audioCtxRef.current = new Ctx();
        }
        audioCtxRef.current?.resume?.();
      } catch { /* sem áudio no navegador */ }
    };
    window.addEventListener('pointerdown', destravar);
    return () => window.removeEventListener('pointerdown', destravar);
  }, []);

  // dispara quando o total de não lidas sobe (nunca no primeiro carregamento)
  const totalNovasRef = useRef(null);
  const somLigadoRef = useRef(somLigado);
  useEffect(() => { somLigadoRef.current = somLigado; }, [somLigado]);
  useEffect(() => {
    if (conversas.length === 0) return;
    const total = conversas.reduce((s, c) => s + novasDe(c), 0);
    if (totalNovasRef.current !== null && total > totalNovasRef.current
        && somLigadoRef.current) {
      tocarDing();
    }
    totalNovasRef.current = total;
    // aba do navegador, igual ao Slack: bolinha vermelha no favicon + "(N)"
    // no título — visível mesmo com a aba em segundo plano
    desenharFaviconBadge(total);
    document.title = total > 0 ? `(${total}) Comece.dev` : 'Comece.dev';
  }, [conversas, vistos]); // eslint-disable-line react-hooks/exhaustive-deps

  // ao sair da página, a aba volta ao normal (ícone e título originais)
  useEffect(() => () => {
    desenharFaviconBadge(0);
    document.title = 'Comece.dev';
  }, []);

  // ─── mensagens da conversa aberta ───
  const [aberta, setAberta] = useState(null);          // {canal_id, nome}
  const [msgs, setMsgs] = useState([]);
  const [temMais, setTemMais] = useState(false);
  const [carregandoMsgs, setCarregandoMsgs] = useState(false);
  const fimRef = useRef(null);

  // ─── sugestão de resposta com IA ───
  // O backend classifica a última pergunta, consulta o banco DAQUELE cliente
  // e devolve texto + fatos. NADA é enviado aqui — o envio é enviarResposta(),
  // e só acontece no clique do operador.
  const [sug, setSug] = useState(null);            // {carregando} | {dados}
  const [respTexto, setRespTexto] = useState('');
  const respRef = useRef(null);
  // a caixa cresce com o texto (a sugestão da IA tem vários parágrafos e não
  // cabia em 64px): do mínimo até 45% da tela; acima disso, rolagem interna
  useEffect(() => {
    const el = respRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const teto = Math.round(window.innerHeight * 0.45);
    el.style.height = `${Math.min(Math.max(el.scrollHeight + 2, 96), teto)}px`;
    el.style.overflowY = el.scrollHeight + 2 > teto ? 'auto' : 'hidden';
  }, [respTexto]);

  // ─── emoji na resposta: insere onde está o cursor ───
  const [emojiAberto, setEmojiAberto] = useState(false);
  const inserirEmoji = (e) => {
    const el = respRef.current;
    const ini = el?.selectionStart ?? respTexto.length;
    const fim = el?.selectionEnd ?? respTexto.length;
    const novo = respTexto.slice(0, ini) + e + respTexto.slice(fim);
    setRespTexto(novo);
    setEmojiAberto(false);
    setTimeout(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = ini + e.length; } }, 0);
  };

  // ─── mensagem de voz: grava no navegador e vira anexo da resposta ───
  // Sai pelo mesmo caminho do arquivo (files.uploadV2, com confirmação). O
  // navegador grava em WebM/Opus; o Slack mostra como arquivo de áudio.
  const [gravando, setGravando] = useState(false);
  const [gravandoSeg, setGravandoSeg] = useState(0);
  const gravadorRef = useRef(null);
  const gravTimerRef = useRef(null);
  const iniciarGravacao = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const tipo = window.MediaRecorder?.isTypeSupported?.('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const rec = new MediaRecorder(stream, { mimeType: tipo });
      const partes = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) partes.push(ev.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(partes, { type: 'audio/webm' });
        if (blob.size > 0) {
          const nome = `mensagem_de_voz_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.webm`;
          setAnexoResp(new File([blob], nome, { type: 'audio/webm' }));
        }
        clearInterval(gravTimerRef.current); setGravando(false); setGravandoSeg(0);
      };
      gravadorRef.current = rec;
      rec.start();
      setGravando(true); setGravandoSeg(0);
      gravTimerRef.current = setInterval(() => setGravandoSeg((s) => s + 1), 1000);
    } catch {
      setMensagem('Não foi possível acessar o microfone — confira a permissão do navegador');
    }
  };
  const pararGravacao = () => { try { gravadorRef.current?.stop(); } catch { /* ok */ } };
  useEffect(() => () => { clearInterval(gravTimerRef.current); try { gravadorRef.current?.stop(); } catch { /* ok */ } }, []);
  const [enviandoResp, setEnviandoResp] = useState(false);

  const buscarSugestao = useCallback((canalId) => {
    const cid = clienteId;
    setSug({ carregando: true });
    axios.post(`${API}/sugestao/`, { cliente_id: cid, canal_id: canalId },
      { headers: headers() })
      .then((res) => {
        // resposta atrasada de outra conversa/cliente não pode aparecer aqui
        if (clienteIdRef.current !== cid || abertaRef.current?.canal_id !== canalId) return;
        setSug({ dados: res.data });
        // sugestão entendida pré-preenche a caixa; sem dado ("não encontrei")
        // ou não entendida, a caixa fica VAZIA para digitar direto — a frase
        // pronta aparece abaixo, com botão "usar" (pedido do Eleandro, 21/08)
        setRespTexto(res.data.entendeu && !res.data.sem_dado
          ? (res.data.sugestao || '') : '');
      })
      .catch(() => {
        if (abertaRef.current?.canal_id === canalId) setSug(null);
      });
  }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── anexos ───
  // Baixar: via proxy do backend (com o JWT), que busca no Slack e transmite —
  // nada fica salvo no servidor. Imagem/PDF abrem no navegador; o resto baixa.
  const baixarAnexo = (mensagemId, indice, a) => {
    setMensagem(`Buscando "${a.nome || 'arquivo'}" no Slack...`);
    axios.get(`${API}/anexo/`, {
      params: { cliente_id: clienteId, mensagem_id: mensagemId, indice },
      headers: headers(), responseType: 'blob',
    })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const mt = a.mimetype || res.headers['content-type'] || '';
        if (mt.startsWith('image/') || mt === 'application/pdf') {
          window.open(url, '_blank');
        } else {
          const l = document.createElement('a');
          l.href = url;
          l.download = a.nome || 'arquivo';
          l.click();
        }
        setMensagem(`${a.nome || 'Arquivo'} aberto`);
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(async (e) => {
        let msg = 'Não foi possível baixar o anexo';
        try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch { /* ok */ }
        setMensagem(`${msg}`);
      });
  };

  // Enviar arquivo: escolhido pelo clipe, vai junto (ou no lugar) do texto —
  // sempre no MESMO clique de Enviar do operador, nunca automático.
  const [anexoResp, setAnexoResp] = useState(null);
  const arquivoRef = useRef(null);
  const escolherAnexo = (e) => {
    const f2 = e.target.files && e.target.files[0];
    if (!f2) return;
    if (f2.size > 20 * 1024 * 1024) {
      setMensagem('Arquivo muito grande (máx. 20MB)');
    } else {
      setAnexoResp(f2);
    }
    e.target.value = '';
  };

  const enviarResposta = () => {
    if (!aberta || (!respTexto.trim() && !anexoResp)) return;
    setEnviandoResp(true);

    // com arquivo: multipart no endpoint de anexo (o texto vira comentário).
    // Sem otimista aqui: o webhook ecoa a mensagem do Slack em segundos e
    // inserir localmente duplicaria o balão.
    if (anexoResp) {
      const fd = new FormData();
      fd.append('cliente_id', clienteId);
      fd.append('canal_id', aberta.canal_id);
      fd.append('texto', respTexto.trim());
      fd.append('confirmar', '1');       // ESTE clique é a autorização
      fd.append('arquivo', anexoResp);
      axios.post(`${API}/responder-anexo/`, fd, { headers: headers() })
        .then((res) => {
          setMensagem(`${res.data.mensagem}`);
          setAnexoResp(null);
          setSug(null);
          setRespTexto('');
        })
        .catch((e) => setMensagem(`${e.response?.data?.erro || 'Não foi possível enviar o arquivo'}`))
        .finally(() => setEnviandoResp(false));
      return;
    }

    axios.post(`${API}/responder/`, {
      cliente_id: clienteId, canal_id: aberta.canal_id,
      texto: respTexto.trim(),
      confirmar: true,          // ESTE clique é a autorização do operador
    }, { headers: headers() })
      .then((res) => {
        // sem aviso de "enviada": o balão aparecendo na conversa JÁ é a
        // confirmação (pedido do Eleandro, 21/08/2026)
        // aparece na conversa na hora (o webhook ecoa depois; não duplica)
        const agora = new Date();
        setMsgs((m) => [...m, {
          id: `local-${Date.now()}`, msg_id: res.data.msg_id,
          autor: 'Você (via Saber)', autor_tipo: 'equipe',
          texto: respTexto.trim(),
          enviada_em: agora.toLocaleString('pt-BR',
            { day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit' }).replace(',', ''),
          origem: 'app', enviada_por: 'você', anexos: [],
        }]);
        setSug(null);
        setRespTexto('');
        setTimeout(() => fimRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' }), 60);
      })
      .catch((e) => setMensagem(`${e.response?.data?.erro || 'Não foi possível enviar'}`))
      .finally(() => setEnviandoResp(false));
  };

  const abrirConversa = (c) => {
    const cid = clienteId;
    setAberta(c);
    setMsgs([]);
    setCarregandoMsgs(true);
    setSug(null);
    setRespTexto('');
    axios.get(`${API}/mensagens/`, {
      params: { cliente_id: clienteId, canal_id: c.canal_id },
      headers: headers(),
    })
      .then((res) => {
        // resposta atrasada de outro cliente/conversa não pode sobrescrever
        if (clienteIdRef.current !== cid || abertaRef.current?.canal_id !== c.canal_id) return;
        setMsgs(res.data.mensagens || []);
        setTemMais(Boolean(res.data.tem_mais));
        setTimeout(() => fimRef.current?.scrollIntoView({ block: 'end' }), 50);
        // pergunta pendente do colaborador? já pede a sugestão
        const ult = (res.data.mensagens || [])[res.data.mensagens.length - 1];
        if (ult && ult.autor_tipo === 'cliente') buscarSugestao(c.canal_id);
      })
      .catch((e) => setMensagem(`${e.response?.data?.erro || 'Não foi possível abrir a conversa'}`))
      .finally(() => setCarregandoMsgs(false));
  };

  const carregarAnteriores = () => {
    if (!aberta || msgs.length === 0) return;
    const cid = clienteId;
    const canal = aberta.canal_id;
    setCarregandoMsgs(true);
    axios.get(`${API}/mensagens/`, {
      params: { cliente_id: clienteId, canal_id: aberta.canal_id, antes_de: msgs[0].id },
      headers: headers(),
    })
      .then((res) => {
        if (clienteIdRef.current !== cid || abertaRef.current?.canal_id !== canal) return;
        setMsgs(m => [...(res.data.mensagens || []), ...m]);
        setTemMais(Boolean(res.data.tem_mais));
      })
      .catch(() => setMensagem('Não foi possível carregar as mensagens anteriores'))
      .finally(() => setCarregandoMsgs(false));
  };

  // conversa ABERTA está sempre lida: ao abrir (e a cada mensagem que chega
  // com ela aberta), o 'visto' acompanha o total e a bolinha some
  useEffect(() => {
    if (!aberta || !clienteId) return;
    const atual = conversas.find((c) => c.canal_id === aberta.canal_id);
    if (!atual) return;
    setVistos((v) => {
      if ((v[aberta.canal_id] ?? -1) >= (atual.mensagens || 0)) return v;
      const novo = { ...v, [aberta.canal_id]: atual.mensagens || 0 };
      try { localStorage.setItem(chaveVistos, JSON.stringify(novo)); } catch { /* ok */ }
      return novo;
    });
  }, [aberta, conversas, clienteId, chaveVistos]);

  // ─── atualização automática (quase tempo real) ───
  // O webhook grava as mensagens no banco no segundo em que chegam; aqui a
  // página consulta a cada 12s: a lista de conversas (silenciosa, sem spinner)
  // e, na conversa aberta, SÓ o que veio depois da última mensagem
  // (?depois_de=) — nada de recarregar a conversa inteira.
  const abertaRef = useRef(null);
  useEffect(() => { abertaRef.current = aberta; }, [aberta]);
  const msgsRef = useRef([]);
  useEffect(() => { msgsRef.current = msgs; }, [msgs]);
  const corpoChatRef = useRef(null);

  useEffect(() => {
    if (!clienteId) return undefined;
    const t = setInterval(() => {
      // lista: atualiza prévias/contadores sem piscar a tela (resposta que
      // chegar depois de trocar de cliente é descartada)
      axios.get(`${API}/`, { params: { cliente_id: clienteId, ...(contaSel ? { conta: contaSel } : {}) }, headers: headers() })
        .then((res) => {
          if (clienteIdRef.current === clienteId) setConversas(res.data.conversas || []);
        })
        .catch(() => {});   // falha pontual: tenta no próximo tique
      // conversa aberta: anexa só as mensagens novas
      const ab = abertaRef.current;
      const ms = msgsRef.current;
      // cursor = última mensagem com id DE BANCO (numérico). A resposta
      // recém-enviada entra otimista com id 'local-...' — mandar esse id no
      // ?depois_de= quebrava o int() do backend e a conversa aberta parava
      // de atualizar até ser reaberta.
      const ultimaReal = ms.length > 0
        && [...ms].reverse().find((x) => /^\d+$/.test(String(x.id)));
      if (ab && ultimaReal) {
        axios.get(`${API}/mensagens/`, {
          params: { cliente_id: clienteId, canal_id: ab.canal_id,
                    depois_de: ultimaReal.id },
          headers: headers(),
        })
          .then((res) => {
            if (clienteIdRef.current !== clienteId
                || abertaRef.current?.canal_id !== ab.canal_id) return;
            const novas = res.data.mensagens || [];
            if (novas.length === 0) return;
            // só rola para o fim se o usuário JÁ estava no fim — quem está
            // lendo o histórico não pode ser puxado para baixo
            const el = corpoChatRef.current;
            const estavaNoFim = !el
              || (el.scrollHeight - el.scrollTop - el.clientHeight < 140);
            setMsgs((m) => {
              // dedupe por id E por msg_id: a resposta enviada pela página é
              // otimista (id 'local-...'), e o eco do webhook chega com outro
              // id de banco mas o MESMO msg_id — sem isto o balão duplicava
              const ids = new Set(m.map((x) => x.id));
              const mids = new Set(m.map((x) => x.msg_id).filter(Boolean));
              return [...m, ...novas.filter((x) =>
                !ids.has(x.id) && !(x.msg_id && mids.has(x.msg_id)))];
            });
            if (estavaNoFim) {
              setTimeout(() => fimRef.current?.scrollIntoView(
                { block: 'end', behavior: 'smooth' }), 60);
            }
            // pergunta nova do colaborador na conversa aberta: refaz a
            // sugestão da IA (só sugere — enviar continua sendo clique)
            if (novas.some((x) => x.autor_tipo === 'cliente')) {
              buscarSugestao(ab.canal_id);
            }
          })
          .catch(() => {});
      }
    }, 12000);
    return () => clearInterval(t);
  }, [clienteId, contaSel]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── busca no texto ───
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState(null);  // null = sem busca ativa
  const [buscando, setBuscando] = useState(false);
  const buscar = () => {
    const q = busca.trim();
    if (q.length < 2) { setMensagem('Digite ao menos 2 letras para buscar'); return; }
    setBuscando(true);
    axios.get(`${API}/busca/`, { params: { cliente_id: clienteId, q }, headers: headers() })
      .then((res) => setResultados(res.data.resultados || []))
      .catch((e) => setMensagem(`${e.response?.data?.erro || 'Não foi possível buscar'}`))
      .finally(() => setBuscando(false));
  };
  useEffect(() => { setResultados(null); setBusca(''); setAberta(null); }, [clienteId]);

  // ─── atualizar (dispara o sync no servidor e acompanha) ───
  // O POST volta na hora (fire-and-forget); a rodada leva ~1 min por causa do
  // rate limit do Slack. Aqui a página consulta o status a cada 5s e, quando o
  // script termina (grava o conversa_sync_log), recarrega tudo sozinha.
  const [sincronizando, setSincronizando] = useState(false);
  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const sincronizar = () => {
    setSincronizando(true);
    axios.post(`${API}/sincronizar/`, { cliente_id: clienteId }, { headers: headers() })
      .then((res) => {
        setMensagem('Atualização iniciada — a página recarrega sozinha quando terminar (~1 min)');
        const apos = res.data.apos_log_id || 0;
        let tentativas = 0;
        pollRef.current = setInterval(() => {
          tentativas += 1;
          if (tentativas > 60) {   // ~5 min sem resposta: desiste do acompanhamento
            clearInterval(pollRef.current); pollRef.current = null;
            setSincronizando(false);
            setMensagem('A atualização está demorando mais que o normal — recarregue em instantes');
            return;
          }
          axios.get(`${API}/sincronizar/status/`,
            { params: { cliente_id: clienteId, apos_log_id: apos }, headers: headers() })
            .then((st) => {
              if (!st.data.terminou) return;
              clearInterval(pollRef.current); pollRef.current = null;
              setSincronizando(false);
              const n = st.data.mensagens_novas || 0;
              setMensagem(n > 0
                ? `Conversas atualizadas — ${n} mensagem${n === 1 ? '' : 's'} nova${n === 1 ? '' : 's'}`
                : 'Conversas atualizadas — nada novo desde a última rodada');
              carregarConversas();
              carregarClientes();
              if (aberta) abrirConversa(aberta);
            })
            .catch(() => {});   // falha pontual do poll: tenta de novo no próximo tique
        }, 5000);
      })
      .catch((e) => {
        setSincronizando(false);
        setMensagem(`${e.response?.data?.erro || 'Falha ao iniciar a atualização'}`);
      });
  };

  // agrupa mensagens por dia para o separador de data
  const msgsPorDia = React.useMemo(() => {
    const grupos = [];
    msgs.forEach(m => {
      const dia = (m.enviada_em || '').slice(0, 10);
      if (!grupos.length || grupos[grupos.length - 1].dia !== dia) {
        grupos.push({ dia, itens: [] });
      }
      grupos[grupos.length - 1].itens.push(m);
    });
    return grupos;
  }, [msgs]);

  return (
    <div className="bporh-admissoes-page conversas-page">
      {/* conversas-dark: tema escuro no padrão do Slack (só nesta página) */}
      <div className="container-fluid mt-4 shadow p-4 rounded conversas-dark">
        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-comments header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Conversas
              {clienteAtual && <span className="text-muted"> · {clienteAtual.cliente}</span>}
            </h2>
          </div>
          {/* Abas das empresas no próprio cabeçalho: clicar já troca o cliente */}
          <div className="conv-abas" role="tablist" aria-label="Empresas">
            {clientes.flatMap(c => ((c.contas || []).length > 1
              ? c.contas.map(ct => ({ ...c, conta: ct.conta, rotulo: `${c.cliente} · ${ct.operadora}`, conversas: ct.conversas }))
              : [{ ...c, conta: '', rotulo: c.cliente }])).map(c => {
              const ativa = String(c.cliente_id) === String(clienteId) && (c.conta || '') === contaSel;
              return (
                <button key={`${c.cliente_id}-${c.conta || 'geral'}`} type="button" role="tab" aria-selected={ativa}
                  className={`conv-aba ${ativa ? 'ativa' : ''}`}
                  onClick={() => { setClienteId(String(c.cliente_id)); setContaSel(c.conta || ''); }}
                  title={`${c.conversas} conversas · ${Number(c.mensagens || 0).toLocaleString('pt-BR')} mensagens`}>
                  <i className="fas fa-building me-2" aria-hidden="true"></i>
                  {c.rotulo}
                  <span className="conv-aba-n">{c.conversas}</span>
                  {(naoLidasPorCliente[String(c.cliente_id)] || 0) > 0 && (
                    <span className="conv-aba-novas"
                      title={`${naoLidasPorCliente[String(c.cliente_id)]} mensagem(ns) não lida(s)`}>
                      {naoLidasPorCliente[String(c.cliente_id)] > 99 ? '99+' : naoLidasPorCliente[String(c.cliente_id)]}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {clienteId && (
              <button className="btn btn-outline-primary" onClick={sincronizar}
                disabled={sincronizando}
                title="Busca no Slack as mensagens que chegaram desde a última sincronização">
                <i className={`fas ${sincronizando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>
                {sincronizando ? 'Atualizando...' : 'Atualizar conversas'}
              </button>
            )}
          </div>
        </div>

        {clienteAtual && (
          <div className="d-flex gap-2 flex-wrap mb-3">
            <span className="badge fs-6" style={{ background: '#611f69', fontVariantNumeric: 'tabular-nums' }}>
              <i className="fas fa-comments me-1"></i>{clienteAtual.conversas} conversas
            </span>
            <span className="badge bg-secondary fs-6" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {clienteAtual.mensagens.toLocaleString('pt-BR')} mensagens
            </span>
            {clienteAtual.sincronizado_em && (
              <span className="badge bg-light text-muted border fs-6">
                <i className="fas fa-rotate me-1"></i>
                sincronizado em {clienteAtual.sincronizado_em}
              </span>
            )}
            <button type="button"
              className={`badge fs-6 border-0 ${somLigado ? '' : 'text-muted'}`}
              style={{ cursor: 'pointer',
                background: somLigado ? '#1fa971' : 'rgba(255,255,255,.12)' }}
              onClick={alternarSom}
              title={somLigado
                ? 'Som de mensagem nova LIGADO — clique para silenciar'
                : 'Som de mensagem nova desligado — clique para ligar'}>
              <i className={`fas ${somLigado ? 'fa-bell' : 'fa-bell-slash'} me-1`}></i>
              {somLigado ? 'som ligado' : 'sem som'}
            </button>
          </div>
        )}

        {!clienteId ? (
          <div className="alert alert-light border text-center text-muted">
            <i className="fas fa-arrow-up me-2"></i>
            Escolha um cliente para ver as conversas do atendimento.
            {clientes.length === 0 && (
              <div className="small mt-2">
                Nenhum cliente com conversas espelhadas ainda — rode o
                <code className="mx-1">sync_slack.py</code> ou cadastre o token
                em Clientes → Serviços → Chat do cliente.
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Busca no texto de TODAS as conversas do cliente */}
            <div className="row g-2 mb-3">
              <div className="col-md-6">
                <div className="input-group">
                  <input type="text" className="form-control"
                    placeholder="Buscar no texto das conversas (ex.: vale transporte, atestado, férias)..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }} />
                  <button className="btn btn-primary" onClick={buscar} disabled={buscando}>
                    <i className={`fas ${buscando ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'}`}></i>
                  </button>
                  {resultados !== null && (
                    <button className="btn btn-outline-secondary"
                      onClick={() => { setResultados(null); setBusca(''); }}
                      title="Limpar a busca">
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* ─── RESULTADOS DA BUSCA ─── */}
            {resultados !== null ? (
              resultados.length === 0 ? (
                <div className="alert alert-light border">
                  <i className="fas fa-inbox me-2"></i>
                  Nada encontrado para “{busca}” nas conversas de {clienteAtual?.cliente}.
                </div>
              ) : (
                <>
                  <div className="small text-muted mb-2">
                    {resultados.length} mensagem{resultados.length === 1 ? '' : 's'} encontradas —
                    clique para abrir a conversa.
                  </div>
                  <div className="border rounded overflow-hidden mb-3">
                    {resultados.map(r => (
                      <div key={r.id} className="d-flex gap-3 px-3 py-2 border-bottom conv-resultado"
                        style={{ cursor: 'pointer' }}
                        onClick={() => abrirConversa({ canal_id: r.canal_id, nome: r.conversa })}>
                        <div style={{ minWidth: '170px' }}>
                          <div className="fw-bold" style={{ fontSize: '.9rem' }}>{r.conversa}</div>
                          <div className="text-muted" style={{ fontSize: '.75rem', fontVariantNumeric: 'tabular-nums' }}>
                            {r.enviada_em}
                          </div>
                        </div>
                        <div style={{ fontSize: '.88rem' }}>
                          <span className="badge me-2" style={{
                            background: (CORES_AUTOR[r.autor_tipo] || CORES_AUTOR.cliente).bg,
                            color: (CORES_AUTOR[r.autor_tipo] || CORES_AUTOR.cliente).cor,
                            border: '1px solid rgba(255,255,255,.18)',
                            fontSize: '.68rem' }}>
                            {r.autor || '—'}
                          </span>
                          {r.texto}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )
            ) : null}

            {/* ─── LISTA DE CONVERSAS + CONVERSA ABERTA ─── */}
            <div className="row g-3">
              <div className={aberta ? 'col-md-4' : 'col-12'}>
                <input type="text" className="form-control form-control-sm mb-2"
                  placeholder="Filtrar por nome..."
                  value={filtroNome} onChange={(e) => setFiltroNome(e.target.value)} />
                {carregando ? (
                  <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                ) : (
                  <div className="border rounded overflow-auto conv-lista" style={{ maxHeight: 'calc((100vh - 230px) / 0.75)', minHeight: '640px' }}>
                    {conversasFiltradas.map(c => {
                      const novas = novasDe(c);
                      return (
                      <div key={c.canal_id}
                        className={`d-flex justify-content-between align-items-start gap-2 px-3 py-2 border-bottom conv-item ${
                          aberta?.canal_id === c.canal_id ? 'ativa' : ''} ${novas > 0 ? 'nao-lida' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => abrirConversa(c)}>
                        <div style={{ minWidth: 0 }}>
                          <div className="fw-bold" style={{ fontSize: '.92rem' }}>
                            <i className={`fas ${c.tipo === 'canal' ? 'fa-hashtag'
                              : c.tipo === 'grupo' ? 'fa-user-group' : 'fa-user'} me-2 text-muted`}
                              style={{ fontSize: '.75rem' }}></i>
                            {c.nome || c.canal_id}
                          </div>
                          <div className="text-muted text-truncate" style={{ fontSize: '.78rem', maxWidth: '420px' }}>
                            {c.previa || <i>sem prévia</i>}
                          </div>
                        </div>
                        <div className="text-end flex-shrink-0">
                          <div className="text-muted" style={{ fontSize: '.72rem', fontVariantNumeric: 'tabular-nums' }}>
                            {c.ultima_msg_em}
                          </div>
                          {novas > 0 ? (
                            <span className="badge rounded-pill conv-novas"
                              title={`${novas} mensagem${novas === 1 ? '' : 's'} nova${novas === 1 ? '' : 's'} desde a última vez que você abriu`}>
                              {novas > 99 ? '99+' : novas}
                            </span>
                          ) : (
                            <span className="badge" style={{ fontVariantNumeric: 'tabular-nums',
                              background: 'rgba(255,255,255,.14)', color: '#e8d5eb' }}>
                              {c.mensagens}
                            </span>
                          )}
                        </div>
                      </div>
                      );
                    })}
                    {conversasFiltradas.length === 0 && (
                      <div className="text-center text-muted py-4">Nenhuma conversa.</div>
                    )}
                  </div>
                )}
              </div>

              {aberta && (
                <div className="col-md-8">
                  <div className="border rounded d-flex flex-column" style={{ height: 'calc((100vh - 230px) / 0.75)', minHeight: '640px' }}>
                    <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom conv-chat-header">
                      <i className="fas fa-user text-muted"></i>
                      <b>{aberta.nome || aberta.canal_id}</b>
                      <button className="btn btn-sm btn-light border ms-auto"
                        onClick={() => setAberta(null)} title="Fechar">
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                    <div className="flex-grow-1 overflow-auto px-3 py-2 conv-chat-corpo" ref={corpoChatRef}>
                      {temMais && (
                        <div className="text-center mb-2">
                          <button className="btn btn-sm btn-outline-secondary"
                            onClick={carregarAnteriores} disabled={carregandoMsgs}>
                            <i className={`fas ${carregandoMsgs ? 'fa-spinner fa-spin' : 'fa-angles-up'} me-2`}></i>
                            Carregar mensagens anteriores
                          </button>
                        </div>
                      )}
                      {carregandoMsgs && msgs.length === 0 ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                      ) : msgsPorDia.map(g => (
                        <div key={g.dia}>
                          <div className="text-center my-2">
                            <span className="badge conv-dia" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {g.dia.split('-').reverse().join('/')}
                            </span>
                          </div>
                          {g.itens.map(m => {
                            const nossa = m.autor_tipo === 'equipe';
                            const est = CORES_AUTOR[m.autor_tipo] || CORES_AUTOR.cliente;
                            return (
                              <div key={m.id} className={`d-flex mb-2 ${nossa ? 'justify-content-end' : ''}`}>
                                <div style={{
                                  maxWidth: '78%', background: est.bg, color: est.cor,
                                  borderRadius: nossa ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                                  // px "grandes" de propósito: o zoom 0,75 global
                                  // reduz tudo — 17px aqui = ~13px na tela
                                  padding: '10px 14px', fontSize: '17px', lineHeight: 1.45,
                                  border: '1px solid rgba(255,255,255,.06)',
                                  boxShadow: '0 1px 2px rgba(0,0,0,.35)' }}>
                                  <div className="d-flex gap-2 align-items-baseline">
                                    <b style={{ fontSize: '14.5px', opacity: .9 }}>{m.autor || '—'}</b>
                                    <span style={{ fontSize: '13px', opacity: .6, fontVariantNumeric: 'tabular-nums' }}>
                                      {(m.enviada_em || '').slice(11)}
                                    </span>
                                    {m.origem === 'app' && m.enviada_por && (
                                      <span style={{ fontSize: '13px', opacity: .7 }}
                                        title="Enviada pela plataforma Saber">
                                        via Saber · {m.enviada_por}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {m.texto ? emojiSlack(m.texto) : <i style={{ opacity: .6 }}>({m.subtipo || 'sem texto'})</i>}
                                  </div>
                                  {(m.anexos || []).map((a, i) => (a.mimetype || '').startsWith('audio/') ? (
                                    <div key={i}>
                                      <AudioAnexo api={API} headers={headers()} clienteId={clienteId}
                                        mensagemId={m.id} indice={i} anexo={a} />
                                    </div>
                                  ) : (
                                    <div key={i} style={{ fontSize: '14px', opacity: .85, marginTop: 4 }}>
                                      <i className="fas fa-paperclip me-1"></i>
                                      {/* clique = proxy do backend (imagem/PDF abrem aqui;
                                          o arquivo continua morando só no Slack) */}
                                      <button type="button"
                                        onClick={() => baixarAnexo(m.id, i, a)}
                                        style={{ background: 'none', border: 'none', padding: 0,
                                          color: 'inherit', textDecoration: 'underline',
                                          cursor: 'pointer', font: 'inherit' }}
                                        title="Abrir/baixar este arquivo">
                                        {a.nome || 'arquivo'}
                                      </button>
                                      {a.tamanho ? ` (${Math.round(a.tamanho / 1024)} KB)` : ''}
                                      <a href={a.url} target="_blank" rel="noopener noreferrer"
                                        className="ms-2" style={{ color: 'inherit', opacity: .6 }}
                                        title="Abrir no Slack">
                                        <i className="fas fa-arrow-up-right-from-square"
                                          style={{ fontSize: '11px' }}></i>
                                      </a>
                                    </div>
                                  ))}
                                  {(m.reacoes || []).length > 0 && (
                                    <div className="conv-reacoes">
                                      {m.reacoes.map((r) => (
                                        <span key={r.nome} className="conv-reacao" title={`:${r.nome}:`}>
                                          {emojiSlack(`:${r.nome}:`)} {r.n}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                      <div ref={fimRef} />
                    </div>

                    {/* ── RESPONDER (sugestão da IA + caixa de resposta) ──
                        NADA sai sem o clique do operador em "Enviar" — regra
                        do Eleandro (21/08/2026): sugerir sim, enviar só com
                        autorização humana. */}
                    <div className="border-top conv-chat-header px-3 py-2">
                      {sug?.carregando && (
                        <div className="small text-muted mb-2">
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Analisando a conversa e consultando o banco...
                        </div>
                      )}
                      {sug?.dados && !sug.carregando && (
                        <div className="mb-2">
                          {sug.dados.entendeu ? (
                            <div className="small mb-1" style={{ color: '#d4a3dd' }}>
                              <i className="fas fa-wand-magic-sparkles me-2"></i>
                              Sugestão da IA
                              {sug.dados.intencao_rotulo && (
                                <span className="badge ms-2"
                                  style={{ background: 'rgba(212,163,221,.15)', color: '#d4a3dd',
                                    border: '1px solid rgba(212,163,221,.4)', fontSize: '12px' }}>
                                  {sug.dados.intencao}
                                </span>
                              )}
                              {sug.dados.colaborador && (
                                <span className="badge ms-1"
                                  style={{ background: 'rgba(31,169,113,.15)', color: '#4cd7a2',
                                    border: '1px solid rgba(31,169,113,.4)', fontSize: '12px' }}
                                  title="Colaborador identificado no cadastro da Convenia deste cliente">
                                  <i className="fas fa-user-check me-1"></i>identificado
                                </span>
                              )}
                            </div>
                          ) : sug.dados.em_desenvolvimento ? (
                            <div className="small mb-1" style={{ color: '#7fb8f0' }}>
                              <i className="fas fa-person-digging me-2"></i>
                              {sug.dados.mensagem}
                            </div>
                          ) : (
                            <div className="small mb-1" style={{ color: '#f1be48' }}>
                              <i className="fas fa-circle-info me-2"></i>
                              {sug.dados.mensagem}
                            </div>
                          )}
                          {(sug.dados.fatos || []).map((ftx, i) => (
                            <div key={i} className="small text-muted">
                              <i className="fas fa-database me-2" style={{ fontSize: '11px' }}></i>{ftx}
                            </div>
                          ))}
                          {(sug.dados.avisos || []).map((av, i) => (
                            <div key={i} className="small" style={{ color: '#f1be48' }}>
                              <i className="fas fa-triangle-exclamation me-2" style={{ fontSize: '11px' }}></i>{av}
                            </div>
                          ))}
                          {/* ROTEIRO — onde o operador confere. Vem sempre, e é o
                              que resta de útil quando não há sugestão de texto. */}
                          {sug.dados.roteiro && (
                            <div className="mt-2 p-2 rounded"
                              style={{ background: 'rgba(127,184,240,.08)',
                                border: '1px solid rgba(127,184,240,.25)' }}>
                              <div className="small fw-semibold" style={{ color: '#7fb8f0' }}>
                                <i className="fas fa-map-signs me-2"></i>
                                Onde conferir — {sug.dados.roteiro.assunto}
                              </div>
                              {sug.dados.roteiro.tela && sug.dados.roteiro.tela !== '—' && (
                                <div className="small text-muted mt-1">
                                  <i className="fas fa-display me-2" style={{ fontSize: '11px' }}></i>
                                  {sug.dados.roteiro.tela}
                                </div>
                              )}
                              <div className="small text-muted">
                                <i className="fas fa-circle-check me-2" style={{ fontSize: '11px' }}></i>
                                responde: {sug.dados.roteiro.responde}
                              </div>
                              <div className="small" style={{ color: '#f1be48' }}>
                                <i className="fas fa-eye me-2" style={{ fontSize: '11px' }}></i>
                                {sug.dados.roteiro.conferir}
                              </div>
                              {(sug.dados.roteiro.tabelas || []).length > 0 && (
                                <details className="small text-muted mt-1">
                                  <summary style={{ cursor: 'pointer' }}>
                                    de onde o dado sai
                                  </summary>
                                  <ul className="mb-0 ps-3">
                                    {sug.dados.roteiro.tabelas.map((t, i) => (
                                      <li key={i}>{t}</li>
                                    ))}
                                  </ul>
                                </details>
                              )}
                            </div>
                          )}
                          {/* sem assunto certo: mostra o menu do que a IA sabe
                              consultar, para o operador reconhecer o caso */}
                          {(sug.dados.assuntos_consultaveis || []).length > 0 && (
                            <details className="mt-2 small text-muted">
                              <summary style={{ cursor: 'pointer', color: '#7fb8f0' }}>
                                <i className="fas fa-list-check me-2"></i>
                                O que a IA sabe consultar no banco
                                ({sug.dados.assuntos_consultaveis.length} assuntos)
                              </summary>
                              <ul className="mb-0 ps-3 mt-1">
                                {sug.dados.assuntos_consultaveis.map((a, i) => (
                                  <li key={i}>
                                    <b>{a.tela}</b> — {a.responde}
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}
                          {sug.dados.sem_dado && sug.dados.sugestao && (
                            <div className="small mt-1 d-flex align-items-center gap-2"
                              style={{ color: '#7fb8f0' }}>
                              <i className="fas fa-comment-dots"></i>
                              <span>Resposta pronta, se quiser: “{sug.dados.sugestao}”</span>
                              <button type="button" className="btn btn-sm btn-light border py-0 px-2"
                                style={{ fontSize: '12px' }}
                                onClick={() => setRespTexto(sug.dados.sugestao)}
                                title="Copia esta frase para a caixa de resposta">
                                usar
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {anexoResp && (
                        <div className="small mb-1 d-flex align-items-center gap-2"
                          style={{ color: '#4cd7a2' }}>
                          <i className="fas fa-paperclip"></i>
                          {anexoResp.name} ({Math.round(anexoResp.size / 1024)} KB)
                          <button type="button" className="btn btn-sm btn-light border p-0 px-1"
                            onClick={() => setAnexoResp(null)} title="Remover o anexo">
                            <i className="fas fa-times" style={{ fontSize: '11px' }}></i>
                          </button>
                        </div>
                      )}
                      <div className="d-flex gap-2 align-items-end">
                        <input type="file" ref={arquivoRef} style={{ display: 'none' }}
                          onChange={escolherAnexo} />
                        <button type="button" className="btn btn-sm btn-light border"
                          disabled={enviandoResp}
                          onClick={() => arquivoRef.current?.click()}
                          title="Anexar um arquivo à resposta (máx. 20MB)">
                          <i className="fas fa-paperclip"></i>
                        </button>
                        <div className="d-flex flex-column gap-1">
                          <button type="button" className="btn btn-sm btn-light border"
                            disabled={enviandoResp}
                            onClick={() => setEmojiAberto((v) => !v)}
                            title="Inserir emoji">
                            <i className="far fa-smile"></i>
                          </button>
                          <button type="button"
                            className={`btn btn-sm border ${gravando ? 'btn-danger' : 'btn-light'}`}
                            disabled={enviandoResp || !!anexoResp}
                            onClick={gravando ? pararGravacao : iniciarGravacao}
                            title={gravando ? 'Parar e anexar a gravação' : 'Gravar mensagem de voz'}>
                            <i className={`fas ${gravando ? 'fa-stop' : 'fa-microphone'}`}></i>
                            {gravando && <span className="ms-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{gravandoSeg}s</span>}
                          </button>
                        </div>
                        {emojiAberto && (
                          <div className="conv-emoji-picker">
                            {EMOJIS_RAPIDOS.map((e) => (
                              <button key={e} type="button" onClick={() => inserirEmoji(e)} title={e}>{e}</button>
                            ))}
                          </div>
                        )}
                        <textarea className="form-control form-control-sm"
                          ref={respRef}
                          rows={4}
                          style={{ minHeight: '96px', resize: 'vertical', lineHeight: 1.45 }}
                          placeholder={sug?.carregando
                            ? 'Aguarde a sugestão...'
                            : 'Escreva a resposta (ou edite a sugestão acima de enviar)...'}
                          value={respTexto} disabled={enviandoResp}
                          onChange={(e) => setRespTexto(e.target.value)} />
                        <div className="d-flex flex-column gap-1">
                          <button type="button" className="btn btn-sm btn-success"
                            style={{ whiteSpace: 'nowrap' }}
                            disabled={enviandoResp || (!respTexto.trim() && !anexoResp)}
                            onClick={enviarResposta}
                            title="Envia ao colaborador no Slack — nada é enviado sem este clique">
                            <i className={`fas ${enviandoResp ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-1`}></i>
                            Enviar
                          </button>
                          {sug?.dados && (
                            <button type="button" className="btn btn-sm btn-outline-secondary"
                              disabled={enviandoResp}
                              onClick={() => { setSug(null); setRespTexto(''); }}
                              title="Descarta a sugestão">
                              <i className="fas fa-times me-1"></i>Limpar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BpoRhConversas;
